import { useApi } from '@/api';
import { ProgressBar } from '@/components';
import type { ProgressBarProps } from '@/components/ProgressBar';
import { useChartTooltipSpk } from '@/hooks';
import {
  Card,
  CurrencyValue,
  DoughnutChart,
  Flex,
  formatPercentage,
  Heading,
  hexToRgb,
  TableSkeleton,
  useIsMobile
} from '@blockanalitica/ui';
import { ReactNode, useMemo } from 'react';

type TreasuryAssetData = {
  symbol: string;
  amount: string;
  percent_of_total: string;
  color: string;
};

interface ProgressData extends ProgressBarProps {
  text: string | ReactNode;
  symbol: string;
  percent: number;
}
export default function MigrationBreakdown() {
  const isMobile = useIsMobile();
  const [data, error] = useApi<TreasuryAssetData[]>({
    url: '/migrations/migration/share/'
  });

  const processedData: ProgressData[] | null = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }
    // Sort data by percentage in descending order
    const sortedData = [...data].sort(
      (a, b) => Number(b.percent_of_total) - Number(a.percent_of_total)
    );

    // Split into top 5 and others and transform them
    const topFive = sortedData.slice(0, 5).map((item) => ({
      text: <Flex gap="xs">{item.symbol}</Flex>,
      symbol: item.symbol,
      percent: Number(item.percent_of_total),
      additionalValue: <CurrencyValue value={item.amount} />,
      color: item.color
    }));

    return topFive;
  }, [data]);

  const customTooltip = useChartTooltipSpk(
    processedData?.map(({ text, percent, additionalValue }) => ({
      text,
      percent,
      additionalValue
    })),
    'text',
    'percent',
    'additionalValue'
  );
  const customConfig = useMemo(
    () => ({
      options: {
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            position: 'bottom' as const
          },
          tooltip: {
            enabled: false,
            position: 'nearest' as const,
            external: (context) => customTooltip(context)
          }
        }
      }
    }),
    [customTooltip]
  );

  const chartData = useMemo(() => {
    if (!processedData) {
      return;
    }

    const labels: string[] = [];
    const backgroundColor: string[] = [];
    const colorsBorder: string[] = [];
    const datasetData: number[] = [];

    processedData.forEach((row) => {
      const { r, g, b } = hexToRgb(row.color);
      labels.push(row.symbol);
      backgroundColor.push(`rgba(${r}, ${g}, ${b}, 0.5)`);
      colorsBorder.push(`rgba(${r}, ${g}, ${b}, 1)`);
      datasetData.push(row.percent);
    });

    return {
      labels,
      backgroundColor,
      colorsBorder,
      datasets: [
        {
          data: datasetData,
          hoverOffset: 15
        }
      ]
    };
  }, [processedData]);

  if (error) {
    throw error;
  }
  if (!data) {
    // TODO: wrong skeleton
    return <TableSkeleton rows={10} columns={6} />;
  }

  if (!processedData || !chartData) {
    return null;
  }

  return (
    <Card flexDirection="column" gap="s">
      <Heading size="4">MKR to SKY Upgrade Breakdown</Heading>
      <Flex gap="s">
        <Flex flexDirection="column" gap="m" flex="1">
          {processedData.map((row) => (
            <ProgressBar key={row.symbol} {...row} />
          ))}
        </Flex>

        {!isMobile ? (
          <Card flexDirection="column" gap="m" backgroundColor="transparent">
            {/* 
                DoughnutChart needs to be wrapped in Flex (technically div) in order
                for the custom tooltip to attach to the correct parent
              */}
            <Flex>
              <DoughnutChart
                config={customConfig}
                data={chartData}
                valueFormatter={formatPercentage}
              />
            </Flex>
          </Card>
        ) : null}
      </Flex>
    </Card>
  );
}
